import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { webSessionStorage } from '../lib/webSessionStorage';
import type { GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';

export type ReceiptFilter = 'all' | 'phr' | 'thr';
export type RecoverableFilter = 'all' | 'recoverable' | 'nonRecoverable';
export type CatalogType = 'PnP' | 'Buy Program' | 'Stock';

export interface AllocationPreferences {
  receiptFilter: ReceiptFilter;
  typeFilters: CatalogType[];
  recoverableFilter: RecoverableFilter;
  search: string;
  sortModel: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  pageSize: number;
}

export type IeView = 'shortages' | 'overages' | 'unauthorized' | 'all';

export interface IePreferences {
  view: IeView;
  search: string;
  sortModel: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  pageSize: number;
}

export interface FrontWindowPreferences {
  allocation: AllocationPreferences;
  ie: IePreferences;
}

export type AllocationPreferencesUpdate =
  | Partial<AllocationPreferences>
  | ((prev: AllocationPreferences) => Partial<AllocationPreferences> | AllocationPreferences);

export type IePreferencesUpdate =
  | Partial<IePreferences>
  | ((prev: IePreferences) => Partial<IePreferences> | IePreferences);

const STORAGE_KEY = 'ui.front-window.preferences.v2';

export const ALLOCATION_DEFAULT_SORT_MODEL: GridSortModel = [
  { field: 'handReceipt', sort: 'desc' },
];

export const getDefaultAllocationSortModel = (): GridSortModel =>
  ALLOCATION_DEFAULT_SORT_MODEL.map((entry) => ({ ...entry }));

export const DEFAULT_TYPE_FILTERS: CatalogType[] = ['Buy Program', 'Stock'];

const VALID_PAGE_SIZES = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

export const createDefaultAllocationPreferences = (): AllocationPreferences => ({
  receiptFilter: 'all',
  typeFilters: [...DEFAULT_TYPE_FILTERS],
  recoverableFilter: 'all',
  search: '',
  sortModel: getDefaultAllocationSortModel(),
  columnVisibility: {},
  pageSize: DEFAULT_PAGE_SIZE,
});

export const createDefaultIePreferences = (): IePreferences => ({
  view: 'shortages',
  search: '',
  sortModel: [],
  columnVisibility: {},
  pageSize: DEFAULT_PAGE_SIZE,
});

export const createDefaultFrontWindowPreferences = (): FrontWindowPreferences => ({
  allocation: createDefaultAllocationPreferences(),
  ie: createDefaultIePreferences(),
});

const normalizeSortModel = (model?: GridSortModel): GridSortModel => {
  if (!Array.isArray(model)) {
    return [];
  }

  return model
    .filter(
      (entry) =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof entry.field === 'string' &&
        entry.field.length > 0,
    )
    .map((entry) => ({
      field: entry.field,
      sort: entry.sort ?? 'asc',
    }));
};

const normalizeColumnVisibility = (
  model?: GridColumnVisibilityModel,
): GridColumnVisibilityModel => {
  if (!model || typeof model !== 'object') {
    return {};
  }

  return Object.entries(model).reduce<GridColumnVisibilityModel>((acc, [field, visible]) => {
    if (typeof field === 'string' && typeof visible === 'boolean') {
      acc[field] = visible;
    }
    return acc;
  }, {});
};

const normalizeRecoverableFilter = (value?: unknown): RecoverableFilter => {
  if (value === true) return 'recoverable';
  if (value === false) return 'all';
  if (value === 'recoverable') return 'recoverable';
  if (value === 'nonRecoverable') return 'nonRecoverable';

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'recoverable') return 'recoverable';
    if (normalized === 'nonrecoverable' || normalized === 'non-recoverable') {
      return 'nonRecoverable';
    }
  }

  return 'all';
};

const normalizeTypeFilters = (value?: unknown): CatalogType[] => {
  if (!Array.isArray(value)) {
    return [...DEFAULT_TYPE_FILTERS];
  }

  const next: CatalogType[] = [];

  value.forEach((entry) => {
    if (entry === 'PnP' || entry === 'Buy Program' || entry === 'Stock') {
      if (!next.includes(entry)) {
        next.push(entry);
      }
    }
  });

  return next.length > 0 ? next : [...DEFAULT_TYPE_FILTERS];
};

const normalizeIeView = (value?: unknown): IeView => {
  if (value === 'shortages' || value === 'overages' || value === 'unauthorized' || value === 'all') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'overages') return 'overages';
    if (normalized === 'unauthorized') return 'unauthorized';
    if (normalized === 'all') return 'all';
  }

  return 'shortages';
};

const normalizePageSize = (value?: unknown): number => {
  if (typeof value === 'number' && (VALID_PAGE_SIZES as readonly number[]).includes(value)) {
    return value;
  }
  return DEFAULT_PAGE_SIZE;
};

const resolveAllocationUpdate = (
  prev: AllocationPreferences,
  update: AllocationPreferencesUpdate,
): AllocationPreferences => {
  const result = typeof update === 'function' ? update(prev) : update;
  const patch = result ?? {};
  const base: AllocationPreferences = {
    ...prev,
    ...patch,
  };

  const hasSortModel = patch && Object.prototype.hasOwnProperty.call(patch, 'sortModel');
  const hasColumnVisibility =
    patch && Object.prototype.hasOwnProperty.call(patch, 'columnVisibility');

  const sortModel = normalizeSortModel(hasSortModel ? patch.sortModel : base.sortModel);
  const columnVisibility = normalizeColumnVisibility(
    hasColumnVisibility ? patch.columnVisibility : base.columnVisibility,
  );
  const effectiveSortModel = sortModel.length > 0 ? sortModel : getDefaultAllocationSortModel();
  const recoverableFilter = normalizeRecoverableFilter(base.recoverableFilter);
  const typeFilters = normalizeTypeFilters(base.typeFilters);
  const pageSize = normalizePageSize(base.pageSize);

  return {
    ...base,
    typeFilters,
    sortModel: effectiveSortModel,
    columnVisibility,
    recoverableFilter,
    pageSize,
  };
};

const resolveIeUpdate = (prev: IePreferences, update: IePreferencesUpdate): IePreferences => {
  const result = typeof update === 'function' ? update(prev) : update;
  const patch = result ?? {};
  const base: IePreferences = {
    ...prev,
    ...patch,
  };

  const hasSortModel = patch && Object.prototype.hasOwnProperty.call(patch, 'sortModel');
  const hasColumnVisibility =
    patch && Object.prototype.hasOwnProperty.call(patch, 'columnVisibility');

  const sortModel = normalizeSortModel(hasSortModel ? patch.sortModel : base.sortModel);
  const columnVisibility = normalizeColumnVisibility(
    hasColumnVisibility ? patch.columnVisibility : base.columnVisibility,
  );
  const view = normalizeIeView(base.view);
  const pageSize = normalizePageSize(base.pageSize);

  return {
    ...base,
    view,
    sortModel,
    columnVisibility,
    pageSize,
  };
};

type FrontWindowPreferencesState = {
  userPreferences: Record<string, FrontWindowPreferences>;
  setAllocationPreferences: (userId: string, update: AllocationPreferencesUpdate) => void;
  setIePreferences: (userId: string, update: IePreferencesUpdate) => void;
  resetUserPreferences: (userId: string) => void;
};

export const useFrontWindowPreferencesStore = create<FrontWindowPreferencesState>()(
  persist(
    (set) => ({
      userPreferences: {},
      setAllocationPreferences: (userId, update) => {
        if (!userId) {
          return;
        }

        set((state) => {
          const existing = state.userPreferences[userId] ?? createDefaultFrontWindowPreferences();
          const nextAllocation = resolveAllocationUpdate(existing.allocation, update);

          return {
            userPreferences: {
              ...state.userPreferences,
              [userId]: {
                ...existing,
                allocation: nextAllocation,
              },
            },
          };
        });
      },
      setIePreferences: (userId, update) => {
        if (!userId) {
          return;
        }

        set((state) => {
          const existing = state.userPreferences[userId] ?? createDefaultFrontWindowPreferences();
          const nextIe = resolveIeUpdate(existing.ie, update);

          return {
            userPreferences: {
              ...state.userPreferences,
              [userId]: {
                ...existing,
                ie: nextIe,
              },
            },
          };
        });
      },
      resetUserPreferences: (userId) => {
        if (!userId) {
          return;
        }

        set((state) => {
          if (!state.userPreferences[userId]) {
            return state;
          }

          const next = { ...state.userPreferences };
          delete next[userId];

          return {
            userPreferences: next,
          };
        });
      },
    }),
    {
      name: STORAGE_KEY,
      version: 2,
      storage: webSessionStorage,
      partialize: (state) => ({
        userPreferences: state.userPreferences,
      }),
      merge: (persisted, current) => {
        const persistedState = persisted as FrontWindowPreferencesState | undefined;
        if (!persistedState || !persistedState.userPreferences) {
          return current;
        }

        const merged: Record<string, FrontWindowPreferences> = {};

        for (const [userId, prefs] of Object.entries(persistedState.userPreferences)) {
          merged[userId] = {
            allocation: resolveAllocationUpdate(
              createDefaultAllocationPreferences(),
              prefs.allocation,
            ),
            ie: resolveIeUpdate(createDefaultIePreferences(), prefs.ie),
          };
        }

        return {
          ...current,
          userPreferences: merged,
        };
      },
    },
  ),
);
